/**
 * Human-scaled timestamps: the closer something is, the less date you need.
 *
 * A conversation is read in the present tense, so "16:57" is the useful fact
 * about a message from today and the date is noise; a month later the reverse is
 * true. This collapses that into one rule set (operator, 2026-08-13):
 *
 *   today            → 16:57
 *   yesterday        → ontem
 *   2-6 days ago     → anteontem / há 3 dias
 *   earlier this year→ 13 de ago
 *   another year     → 13 de ago de 2025
 *
 * ⚠ Buckets are CALENDAR days, not 24-hour spans. 23:50 → 00:10 is "yesterday"
 * to a reader even though it is twenty minutes ago, and a 24h-delta version of
 * this would call it "today" — which reads as wrong precisely when someone
 * checks it at midnight.
 *
 * Wording comes from `Intl.RelativeTimeFormat` with `numeric: "auto"`, so each
 * locale supplies its own idiom (pt-BR gets "ontem"/"anteontem", en-US
 * "yesterday"/"2 days ago") instead of this file hardcoding phrases it would
 * then have to translate three ways.
 */

/** Whole calendar days between two instants, by local midnight. */
function calendarDaysBetween(from: Date, to: Date): number {
  const a = new Date(from.getFullYear(), from.getMonth(), from.getDate()).getTime();
  const b = new Date(to.getFullYear(), to.getMonth(), to.getDate()).getTime();
  // Divide a whole-day difference — DST shifts a day by an hour, and flooring a
  // raw ms/86400000 would land a day early twice a year.
  return Math.round((b - a) / 86_400_000);
}

export function formatMessageTimestamp(ms: number, locale: string, now: Date = new Date()): string {
  const d = new Date(ms);
  const daysAgo = calendarDaysBetween(d, now);

  if (daysAgo <= 0) {
    // Today (and anything the clock says is ahead of us — a small skew must not
    // print "in 1 day"; the time is still the honest thing to show).
    return d.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
  }
  if (daysAgo < 7) {
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
    return rtf.format(-daysAgo, "day");
  }
  const sameYear = d.getFullYear() === now.getFullYear();
  return d.toLocaleDateString(locale, {
    day: "numeric",
    month: "short",
    ...(sameYear ? {} : { year: "numeric" }),
  });
}

/** Full date + time, for the `title` tooltip — the display above is lossy by
 *  design, so precision stays one hover away rather than being discarded. */
export function formatMessageTimestampFull(ms: number, locale: string): string {
  return new Date(ms).toLocaleString(locale, { dateStyle: "long", timeStyle: "short" });
}
